import { Icon, Tooltip } from '~/components';
import projectStatuses from '~/lookups/project-statuses';
import React from 'react';
import styled from 'styled-components';
import { colors } from '~/styles';
import ProjectPopover from '../../components/ProjectPopover';
import { Byline, Label, Assignment } from '../../components/schedule/sidebar';
import SidebarMenu from '../../components/schedule/sidebar/SidebarMenu';
import _ from 'lodash';
import { Action, Actions, Box, Content, Row as RowContainer } from '../../components/schedule/sidebar/SidebarStyles';
import { useApi, useWorkspace } from '~/contexts';

const RowActions = styled(Actions)`
  flex-direction: row;
`;

const RowAction = styled(Action)`
  width: 1rem;
  margin-left: 0.5rem;
`;

const TooltipContainer = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  pointer-events: none;

  &::after {
    content: ' ';
    opacity: 0.25;
    height: 100%;
    width: 100%;
    background: repeating-linear-gradient(
      45deg,
      ${colors.grey10},
      ${colors.grey10} 5px,
      ${colors.white} 5px,
      ${colors.white} 10px
    );
  }

  > div {
    position: absolute;
    right: 2rem;
    pointer-events: all;
    margin-left: auto;
    padding-right: 0.75rem;
  }
`;

export default function Row({ row, options, onReassignAllocations }) {
  const { workspace } = useWorkspace();
  const api = useApi();

  if (!row.assignment) return null;

  return (
    <RowContainer key={row.id} style={row.meta.style} data-testid={`row_${row.id}`} data-testid-lanes={row.meta.lanes}>
      <Box>
        <Content>
          <Assignment>
            {{
              project: () => (
                <>
                  <Label>
                    <ProjectPopover projectId={row.assignment.id}>{row.assignment.name}</ProjectPopover>
                  </Label>

                  {row.assignment.client && <Byline>{row.assignment.client.name}</Byline>}

                  <Info row={row} options={options} />
                </>
              ),
              time_off: () => <Label>{row.assignment.name}</Label>,
            }[row.assignment.assignmentType]()}
          </Assignment>
        </Content>

        {_.isFunction(onReassignAllocations) && (
          <RowActions>
            <RowAction>
              <SidebarMenu>
                {({ setIsOpen }) => {
                  const handleReassignAllocations = async () => {
                    setIsOpen(false);
                    await onReassignAllocations(row.assignment, row.resource);
                  };

                  return (
                    <SidebarMenu.Item
                      onClick={handleReassignAllocations}
                      onMount={async (setState) => {
                        setState((state) => ({ ...state, disabled: true }));

                        try {
                          const { data: manageAllocations } = await api.www
                            .workspaces(workspace.id)
                            .allocations()
                            .memberSchedule.manageProjectAllocations(row.assignment.id);

                          setState((state) => ({
                            ...state,
                            disabled: !manageAllocations,
                            tooltip: !manageAllocations
                              ? 'Insufficient permissions to reassign allocations on this project.'
                              : null,
                          }));
                        } catch {
                          // Do nothing
                        }
                      }}>
                      Reassign Allocations to a Member
                    </SidebarMenu.Item>
                  );
                }}
              </SidebarMenu>
            </RowAction>
          </RowActions>
        )}
      </Box>
    </RowContainer>
  );
}

function Info({ row, options }) {
  if (options.showAssigned && !row.assignment.assigned) {
    return (
      <TooltipContainer>
        <Tooltip message="You are not currently assigned to this project.">
          <Icon icon="info-circle" color={colors.grey25} />
        </Tooltip>
      </TooltipContainer>
    );
  }

  if (options.showTentative && row.assignment.statusId === projectStatuses.tentative.id) {
    return (
      <TooltipContainer>
        <Tooltip message="This is a tentative project.">
          <Icon icon="info-circle" color={colors.grey25} />
        </Tooltip>
      </TooltipContainer>
    );
  }

  return null;
}
